import { useState, useRef, useEffect } from 'react';
import { IconClock } from '@tabler/icons-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

function parse24(val) {
  if (!val) return { hour: null, minute: null, period: 'AM' };
  const [h, m] = val.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { hour, minute: m, period };
}

function to24(hour, minute, period) {
  let h = hour;
  if (period === 'AM' && h === 12) h = 0;
  else if (period === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatDisplay(val) {
  if (!val) return null;
  const { hour, minute, period } = parse24(val);
  if (hour === null) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
}

export default function TimePicker({ value, onChange, placeholder = 'সময় বেছে নিন', className = '' }) {
  const [open, setOpen] = useState(false);
  const { hour: selHour, minute: selMinute, period: selPeriod } = parse24(value);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const handleSelect = (hour, minute, period) => {
    const h = hour ?? selHour ?? 9;
    const m = minute ?? selMinute ?? 0;
    const p = period ?? selPeriod ?? 'AM';
    onChange(to24(h, m, p));
  };

  // Scroll selected items into view when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        hourRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center', behavior: 'instant' });
        minuteRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }, 50);
    }
  }, [open]);

  const display = formatDisplay(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full px-3.5 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/40 bg-white dark:bg-zinc-800 text-left flex items-center justify-between transition-shadow ${className}`}
        >
          <span className={display ? 'text-zinc-800 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}>
            {display || placeholder}
          </span>
          <IconClock size={16} className="text-zinc-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <div className="flex divide-x divide-zinc-200 dark:divide-zinc-700">
          {/* Hours */}
          <ScrollArea className="h-52 flex-1" ref={hourRef}>
            <div className="p-1">
              {HOURS.map(h => (
                <button
                  key={h}
                  type="button"
                  data-selected={selHour === h}
                  onClick={() => handleSelect(h, null, null)}
                  className={`w-full px-2 py-1.5 text-sm rounded-lg text-center transition-colors ${
                    selHour === h
                      ? 'bg-primary text-white font-semibold'
                      : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
            </div>
          </ScrollArea>

          {/* Minutes */}
          <ScrollArea className="h-52 flex-1" ref={minuteRef}>
            <div className="p-1">
              {MINUTES.map(m => (
                <button
                  key={m}
                  type="button"
                  data-selected={selMinute === m}
                  onClick={() => handleSelect(null, m, null)}
                  className={`w-full px-2 py-1.5 text-sm rounded-lg text-center transition-colors ${
                    selMinute === m
                      ? 'bg-primary text-white font-semibold'
                      : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </ScrollArea>

          {/* AM/PM */}
          <div className="flex flex-col p-1 justify-center gap-1">
            {['AM', 'PM'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => handleSelect(null, null, p)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  selPeriod === p
                    ? 'bg-primary text-white'
                    : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
